import { Stack } from "@mui/material";
import React, { type ChangeEvent, type ReactNode } from "react";
import styled from "styled-components";
import { cn } from "utils/styling";

type CheckboxProps = {
  checked?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

const BOX = "var(--dimension-height-1x)";

export const Checkbox = ({ checked, children, className, disabled, onChange }: CheckboxProps) => {
  return (
    <Wrapper className={className}>
      <label className="container">
        <span className="control">
          <input checked={checked} disabled={disabled} onChange={onChange} type="checkbox" />
          <span aria-hidden className="checkmark" />
        </span>
        {children != null && <div className="label-text">{children}</div>}
      </label>
    </Wrapper>
  );
};

export const FilledCheckbox = ({ checked, children, className, disabled, onChange }: CheckboxProps) => {
  return (
    <label
      className={cn(
        "relative inline-flex cursor-pointer items-center gap-2",
        disabled && "cursor-not-allowed opacity-60",
        className
      )}
    >
      <Stack
        className={cn(
          " h-5 w-5 justify-center rounded-ds-0-25x transition-colors",
          checked && "bg-lds-element-primary",
          !checked && "bg-lds-surface-bright border-ds-strong"
        )}
      >
        <svg
          className={cn("h-4 w-4 text-lds-element-primary-on", !checked && "invisible")}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={3.5}
          viewBox="0 0 24 24"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      </Stack>
      <input checked={checked} className="sr-only" disabled={disabled} onChange={onChange} type="checkbox" />
      {children != null && <span className="font-ds-body-regular-sm text-lds-foreground-primary">{children}</span>}
    </label>
  );
};

const Wrapper = styled.span`
  display: block;
  position: relative;
  font-size: 22px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  label.container {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }

  label.container:has(input:disabled) {
    cursor: not-allowed;
  }

  .control {
    position: relative;
    flex-shrink: 0;
    width: ${BOX};
    height: ${BOX};
  }

  /* Hit target + keyboard focus; visual box is .checkmark below */
  input {
    position: absolute;
    inset: 0;
    z-index: 1;
    margin: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  input:disabled {
    cursor: not-allowed;
  }

  .checkmark {
    position: absolute;
    left: 0;
    box-sizing: border-box;
    width: ${BOX};
    height: ${BOX};
    border: 1px solid var(--color-border-strong);
    border-radius: var(--border-radius-0-25x);
    background-color: var(--color-surface-bright);
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, border 0.15s ease;
  }

  .label-text {
    color: var(--color-foreground-primary);
    display: flex;
    align-items: center;
  }

  /* ——— Not selected ——— */
  label:hover .control input:not(:checked):not(:disabled) ~ .checkmark {
    background-color: var(--color-surface-bright);
    border-color: var(--color-border-focus);
  }

  .control input:not(:checked):focus-visible ~ .checkmark {
    background-color: var(--color-element-secondary-initial);
    border: var(--border-focus-strong);
  }

  .control input:not(:checked):disabled ~ .checkmark {
    background-color: var(--color-surface-bright);
    border: 1px solid var(--color-border-subtle);
  }

  /* ——— Selected ——— */
  .control input:checked ~ .checkmark {
    background-color: var(--color-surface-bright);
    border: 1px solid var(--color-border-accent);
  }

  label:hover .control input:checked:not(:disabled) ~ .checkmark {
    background-color: var(--color-surface-bright);
    border: 1px solid var(--color-border-focus);
  }

  .control input:checked:focus-visible ~ .checkmark {
    background-color: var(--color-element-secondary-initial);
    border: var(--border-focus-strong);
  }

  .control input:checked:disabled ~ .checkmark {
    background-color: var(--color-selection-subtle);
    border: 1px solid var(--color-border-subtle);
  }

  .control input:checked:disabled ~ .checkmark:after {
    border-color: var(--color-foreground-disabled);
  }

  /* Check icon */
  .checkmark:after {
    content: "";
    position: absolute;
    left: 4px;
    top: 0;
    display: none;
    width: 4.5px;
    height: 9px;
    border: 1px solid var(--color-element-primary-initial);
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }

  .control input:checked ~ .checkmark:after {
    display: block;
  }

  input:focus-visible {
    outline: none;
  }
`;
